import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import * as S from '@/components/Ad/Complaint/Complaint.styles';
import { Popup } from '@/components/UI/Popup/Popup';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { BottomSheet } from '@/components/UI/BottomSheet/BottomSheet';
import { Button } from '@/components/UI/Button/Button';
import {
  AngleBackIcon,
  FlagFilledIcon,
  FlagIcon,
} from '@/components/UI/Icons/Icons';
import { getLoginUrlWithReturn, sleep } from '@/utils/helpers';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { Textarea } from '@/components/UI/Textarea/Textarea';
import {
  ComplaintFields,
  complaintSchema,
} from '@/components/Ad/Complaint/Complaint.types';
import { ReportReason } from '@/api/types/ads.types';
import { CREATE_REPORT } from '@/api/mutations/ads.mutations';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import { QUERY } from '@/constants/common';
import { useAuthStore } from '@/store/useAuthStore';
import { useAdDetails } from '@/views/Ads/useAdDetails';
import { Divider } from '@/components/UI/Divider/Divider.styles';
import { MODAL_ANIMATION_DURATION } from '@/styles/constants';
import { CreateReportResponse } from '@/api/types/common.types';
import { useBodyOverflow } from '@/hooks/useBodyOverflow';
import { useServerDataContext } from '@/contexts';

export const Complaint = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const customPush = useCustomPush();
  const router = useRouter();
  const [isPopupOpen, popupToggle] = useToggleOpenState();
  const [isBSOpen, toggleBS] = useToggleOpenState(QUERY.complain);
  const [isComplaintSent, setIsComplaintSent] = useState(false);
  const [stage, setStage] = useState<'reportReason' | 'text'>('reportReason');
  const [reportReason, setReportReason] = useState<ReportReason | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [isAuthenticated] = useAuthStore(state => [state.isAuthenticated]);
  const [errorMsg, setErrorMsg] = useState('');
  const { adDetails } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const adData = adDetails ?? initialAdDetails;
  const { control, handleSubmit, reset, setValue } = useForm<ComplaintFields>({
    resolver: zodResolver(complaintSchema),
    defaultValues: { reportReasonId: '', body: '' },
  });
  useBodyOverflow(isBSOpen);

  const [createReport, { loading }] = useMutation<CreateReportResponse>(
    CREATE_REPORT,
    {
      onCompleted: () => {
        setIsComplaintSent(true);
        toggleBS();
        sleep(MODAL_ANIMATION_DURATION).then(() => popupToggle());
      },
      onError: error => {
        setErrorMsg(error.graphQLErrors?.[0]?.message);
      },
    }
  );

  function startComplain() {
    if (!isAuthenticated) {
      const cleanPath = router.asPath.split('#')[0];

      customPush(
        getLoginUrlWithReturn(`${cleanPath}#${QUERY.complain}`, router.asPath)
      );

      return;
    }
    toggleBS();
  }

  function onSubmit(data: ComplaintFields) {
    if (!adData?.id) return;

    createReport({
      variables: {
        adId: adData.id,
        reportReasonId: data.reportReasonId,
        body: data.body,
      },
    });
  }

  function selectReasonAndNext(rr: ReportReason) {
    setReportReason(rr);
    setValue('reportReasonId', rr.id);
    setStage('text');
  }

  function resetBody() {
    setValue('body', '');
    setErrorMsg('');
  }

  function goBack() {
    setStage('reportReason');
    setErrorMsg('');
    reset();
  }

  function handleClose() {
    toggleBS();
    sleep(MODAL_ANIMATION_DURATION).then(() => setErrorMsg(''));
  }

  useEffect(() => {
    (async () => {
      if (!isBSOpen) {
        await sleep(500);
        setStage('reportReason');
        reset();
      }
    })();
  }, [isBSOpen]);

  if (!adData) return null;

  return (
    <>
      <S.Box data-testid="complaint-box">
        {isComplaintSent ? (
          <FlagFilledIcon fill={theme.palette.primary.labels} />
        ) : (
          <FlagIcon fill={theme.palette.primary.labels} />
        )}
        <Typography.Body
          data-testid="complaint-title"
          $variant="2"
          $isUnderlined={!isComplaintSent}
          onClick={isComplaintSent ? undefined : startComplain}
        >
          {isComplaintSent ? t('pages.ad.complained') : t('pages.ad.complain')}
        </Typography.Body>
      </S.Box>
      <BottomSheet
        isOpen={isBSOpen}
        close={handleClose}
        {...(stage === 'reportReason' && {
          title: t('pages.ad.complaint_reason'),
        })}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          {stage === 'reportReason' &&
            adData.reportReasons.map(r => (
              <S.Item
                key={r.id}
                onClick={() => selectReasonAndNext(r)}
                data-testid="complaint-report-reason"
              >
                {r.name}
                <AngleBackIcon isRotated />
              </S.Item>
            ))}

          {stage === 'text' && reportReason && (
            <>
              <S.ComplaintBSHead>
                <Button
                  onClick={goBack}
                  appearance="primaryText"
                  hasPadding={false}
                  disabled={loading}
                >
                  {t('common.decline')}
                </Button>
                <S.ComplaintBSTitle>{reportReason.name}</S.ComplaintBSTitle>
                <Button
                  data-testid="complaint-reset"
                  onMouseDown={e => e.preventDefault()}
                  onClick={resetBody}
                  appearance="primaryText"
                  hasPadding={false}
                  disabled={loading}
                >
                  {t('common.reset')}
                </Button>
              </S.ComplaintBSHead>
              <Controller
                name="body"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    ref={bodyRef}
                    autoFocus
                    onBlur={() => bodyRef.current?.focus()}
                    placeholder={t('pages.ad.complaint_placeholder')}
                    height={120}
                    data-testid="complaint-textarea"
                  />
                )}
              />
              {!!errorMsg && (
                <S.ComplaintBSError>{errorMsg}</S.ComplaintBSError>
              )}
              <S.ComplaintBSSubmit
                type="submit"
                appearance="primaryText"
                hasPadding={false}
                disabled={loading}
                data-testid="complaint-submit"
              >
                {t('common.send')}
              </S.ComplaintBSSubmit>
            </>
          )}
        </form>
      </BottomSheet>
      <Popup
        dataTestId="complaint-popup"
        isOpen={isPopupOpen}
        close={popupToggle}
        icon="check-circle-rounded.svg"
        iconSize={35}
        title={t('pages.ad.complaint_sent')}
        description={t('pages.ad.complaint_sent_subtitle')}
        actions={{ left: { text: t('common.ok'), onClick: popupToggle } }}
      />

      <Divider $isSmall $marginTop="10px" />
    </>
  );
};
